import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Eye, EyeOff, Clock } from "lucide-react";
import { api } from "../utils/api";
import RichTextEditor from "../components/RichTextEditor";
import { renderNewsHtml, isRichTextEmpty } from "../utils/sanitize";
import TeamManager from "../components/TeamManager";
import UserManager from "../components/UserManager";
import ServicesManager from "../components/ServicesManager";
import { ContactManager, HoursManager } from "../components/ContactHoursManager";
import MenopauseManager from "../components/MenopauseManager";
import AboutManager from "../components/AboutManager";
import LegalContactManager from "../components/LegalContactManager";

// Hilfsfunktionen für Datum/Uhrzeit-Handling (datetime-local Format)
function formatDateTimeForDisplay(iso) {
  if (!iso) return "";
  const [d, t] = iso.split("T");
  if (!d || !t) return iso;
  const [y, m, day] = d.split("-");
  return `${day}.${m}.${y}, ${t} Uhr`;
}
function statusLabel(status) {
  return status === "active" ? "aktiv"
       : status === "scheduled" ? "geplant"
       : "abgelaufen";
}

export default function AdminDashboard() {
  const ctx = useOutletContext() || {};
  const currentUser = ctx.user || null;
  const role = (currentUser?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isVisiter = role === "visiter";
  const readOnly = isVisiter; // Visiter darf sehen und Felder bearbeiten, aber nicht speichern/löschen.
  const navigate = useNavigate();

  // Nur Tabs anzeigen die der User sehen darf.
  //  - admin:      alle 9 Tabs
  //  - visiter:    alle außer Menopause + Benutzer
  //  - editor:     News + Sprechzeiten
  const availableTabs = isAdmin
    ? ["news", "hours", "contact", "team", "services", "about", "menopause", "legal", "users"]
    : isVisiter
      ? ["news", "hours", "contact", "team", "services", "about", "legal"]
      : ["news", "hours"];
  const [tab, setTab] = useState("news");

  const roleLabel = isAdmin ? "Admin" : isVisiter ? "Visiter" : "Mitarbeiterin";

  const logout = async () => {
    await api.logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-page" data-testid="admin-dashboard-page">
      <div className="admin-card admin-card-wide">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Praxis-Verwaltung</h1>
            <p className="admin-subtitle">
              Angemeldet als <strong>{currentUser?.username || "?"}</strong>
              {" "}({roleLabel})
            </p>
          </div>
          <div className="admin-header-actions">
            <Link to="/" className="admin-btn admin-btn-ghost" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site">Website ansehen</Link>
            <button type="button" onClick={logout} className="admin-btn admin-btn-ghost" data-testid="admin-logout-btn">Abmelden</button>
          </div>
        </div>

        {readOnly && (
          <div className="admin-readonly-banner" data-testid="admin-readonly-banner">
            <strong>Lesemodus:</strong> Sie können alle Inhalte einsehen. Speichern, Löschen und Neuanlagen sind für die Rolle „Visiter&#8220; deaktiviert.
          </div>
        )}

        <ScrollableTabs
          availableTabs={availableTabs}
          currentTab={tab}
          onTabChange={setTab}
        />

        {tab === "news" && <NewsPanel readOnly={readOnly} />}
        {tab === "team" && (isAdmin || isVisiter) && <TeamManager readOnly={readOnly} />}
        {tab === "services" && (isAdmin || isVisiter) && <ServicesManager readOnly={readOnly} />}
        {tab === "about" && (isAdmin || isVisiter) && <AboutManager readOnly={readOnly} />}
        {tab === "menopause" && isAdmin && <MenopauseManager />}
        {tab === "contact" && (isAdmin || isVisiter) && <ContactManager readOnly={readOnly} />}
        {tab === "hours" && <HoursManager readOnly={readOnly} />}
        {tab === "legal" && (isAdmin || isVisiter) && <LegalContactManager readOnly={readOnly} />}
        {tab === "users" && isAdmin && <UserManager currentUser={currentUser} />}
      </div>
    </div>
  );
}

/* -------- Scrollable Tabs Bar (mit Fade + Auto-Scroll zum aktiven Tab) -------- */
function ScrollableTabs({ availableTabs, currentTab, onTabChange }) {
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);

  const LABELS = {
    news: "News", team: "Team", services: "Leistungen", about: "Über mich",
    menopause: "Menopause", contact: "Kontaktdaten", hours: "Sprechzeiten",
    legal: "Rechtliches & Kontakt", users: "Benutzer",
  };

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    const w = wrapperRef.current;
    if (!el || !w) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    w.classList.toggle("has-overflow-left", scrollLeft > 2);
    w.classList.toggle("has-overflow-right", scrollLeft < maxScroll - 2);
  }, []);

  useEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateFades, { passive: true });
    window.addEventListener("resize", updateFades);
    return () => {
      el.removeEventListener("scroll", updateFades);
      window.removeEventListener("resize", updateFades);
    };
  }, [updateFades, availableTabs]);

  // Aktiven Tab automatisch in Sicht scrollen
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const activeBtn = el.querySelector(`[data-testid="admin-tab-${currentTab}"]`);
    if (activeBtn) {
      const btnLeft = activeBtn.offsetLeft;
      const btnRight = btnLeft + activeBtn.offsetWidth;
      const viewLeft = el.scrollLeft;
      const viewRight = viewLeft + el.clientWidth;
      if (btnLeft < viewLeft) el.scrollTo({ left: btnLeft - 16, behavior: "smooth" });
      else if (btnRight > viewRight) el.scrollTo({ left: btnRight - el.clientWidth + 16, behavior: "smooth" });
    }
  }, [currentTab]);

  return (
    <div ref={wrapperRef} className="admin-tabs-wrapper" data-testid="admin-tabs-wrapper">
      <nav ref={scrollRef} className="admin-tabs" role="tablist" data-testid="admin-tabs">
        {availableTabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={currentTab === t}
            className={`admin-tab${currentTab === t ? " is-active" : ""}`}
            onClick={() => onTabChange(t)}
            data-testid={`admin-tab-${t}`}
          >
            {LABELS[t] || t}
          </button>
        ))}
      </nav>
    </div>
  );
}

/* -------- News-Panel (kompletter alter News-Bereich als Sub-Komponente) -------- */
function NewsPanel({ readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null); // null | Item
  const [serverNow, setServerNow] = useState("");

  const reload = async () => {
    setLoading(true);
    const { data } = await api.listNews();
    setLoading(false);
    if (data && data.ok) {
      setItems(data.items || []);
      setServerNow(data.server_now || "");
    }
    else setError("Konnte Einträge nicht laden.");
  };
  useEffect(() => { reload(); }, []);

  const toLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const startNew = () => {
    setError(""); setMessage("");
    const today = new Date();
    const start = new Date(today); start.setHours(8, 0, 0, 0);
    const end = new Date(today); end.setHours(20, 0, 0, 0);
    setEditing({
      id: null, subject: "", subject_align: "left", body: "",
      start_at: toLocal(start), end_at: toLocal(end),
    });
  };

  const startEdit = (it) => { setError(""); setMessage(""); setEditing({ ...it }); };
  const cancelEdit = () => { setEditing(null); setError(""); setMessage(""); };

  const save = async () => {
    if (!editing) return;
    setError(""); setMessage("");
    const cleanBody = isRichTextEmpty(editing.body) ? "" : editing.body;
    const payload = {
      subject: editing.subject || "",
      subject_align: editing.subject_align || "left",
      body: cleanBody,
      start_at: editing.start_at,
      end_at: editing.end_at,
    };
    const { data, status } = editing.id
      ? await api.updateNews(editing.id, payload)
      : await api.createNews(payload);

    if (data && data.ok) {
      setMessage(editing.id ? "Eintrag aktualisiert." : "Neuer Eintrag angelegt.");
      setEditing(null);
      await reload();
      return;
    }
    if (status === 400 && data && data.message) {
      setError(data.message);
    } else {
      setError("Speichern fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eintrag „${it.subject || "(ohne Betreff)"}" wirklich löschen?`)) return;
    const { data } = await api.deleteNews(it.id);
    if (data && data.ok) { setMessage("Eintrag gelöscht."); await reload(); }
    else setError("Löschen fehlgeschlagen.");
  };

  const duplicate = async (it) => {
    const { data } = await api.duplicateNews(it.id);
    if (data && data.ok) {
      setMessage("Eintrag kopiert (gleicher Zeitraum wie das Original).");
      await reload();
    } else setError("Kopieren fehlgeschlagen.");
  };

  return (
    <div data-testid="admin-news-panel">
      <div style={{ marginBottom: "1rem" }}>
        <h2 className="admin-list-title" style={{ margin: 0 }}>News-Bereich verwalten</h2>
        <p className="admin-align-hint" style={{ margin: "0.3rem 0 0" }}>
          Plane News-Einträge im Voraus. Sie erscheinen automatisch im festgelegten Zeitraum
          auf der Startseite. Mehrere Einträge dürfen gleichzeitig aktiv sein –
          sie werden dann untereinander angezeigt (neuester oben).
        </p>
      </div>

      {message && <p className="admin-success" data-testid="admin-message">{message}</p>}
      {error && <p className="admin-error" data-testid="admin-error">{error}</p>}

      {editing ? (
        <NewsForm item={editing} onChange={setEditing} onSave={save} onCancel={cancelEdit} readOnly={readOnly} serverNow={serverNow} />
      ) : (
        <>
          <div className="admin-list-header">
            <h2 className="admin-list-title">
              {items.length} Eintr{items.length === 1 ? "ag" : "äge"}
            </h2>
            <button type="button" onClick={startNew} className="admin-btn admin-btn-primary" disabled={readOnly} data-testid="admin-news-new">
              + Neuer Eintrag
            </button>
          </div>

          {loading ? <p>Lade…</p> : items.length === 0 ? (
            <p className="admin-empty">Noch keine Einträge angelegt. Klicke oben rechts auf „+ Neuer Eintrag&#8220;.</p>
          ) : (
            <ul className="admin-news-list" data-testid="admin-news-list">
              {items.map((it) => (
                <li key={it.id} className={`admin-news-item status-${it.status}`} data-testid={`admin-news-item-${it.id}`}>
                  <div className="admin-news-item-main">
                    <div className="admin-news-item-header">
                      <span className={`admin-status-badge status-${it.status}`}>{statusLabel(it.status)}</span>
                      <strong className="admin-news-item-subject" style={{ textAlign: it.subject_align }}>
                        {it.subject || <em>(ohne Betreff)</em>}
                      </strong>
                    </div>
                    <div className="admin-news-item-period">
                      <span>Start: {formatDateTimeForDisplay(it.start_at)}</span>
                      <span>Ende: {formatDateTimeForDisplay(it.end_at)}</span>
                    </div>
                    {!isRichTextEmpty(it.body) && (
                      <div className="admin-news-item-preview"
                           dangerouslySetInnerHTML={{ __html: renderNewsHtml(it.body) }} />
                    )}
                  </div>
                  <div className="admin-news-item-actions">
                    <button type="button" onClick={() => startEdit(it)} className="admin-btn admin-btn-ghost" data-testid={`admin-edit-${it.id}`}>Bearbeiten</button>
                    <button type="button" onClick={() => duplicate(it)} className="admin-btn admin-btn-ghost" disabled={readOnly} data-testid={`admin-duplicate-${it.id}`}>Kopieren</button>
                    <button type="button" onClick={() => remove(it)} className="admin-btn admin-btn-danger" disabled={readOnly} data-testid={`admin-delete-${it.id}`}>Löschen</button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <NotificationEmails readOnly={readOnly} />
        </>
      )}
    </div>
  );
}

/* -------- News-Formular (Create + Edit) -------- */
function NewsForm({ item, onChange, onSave, onCancel, readOnly = false, serverNow = "" }) {
  const set = (patch) => onChange({ ...item, ...patch });
  const alignBtn = (val, label, icon) => (
    <button
      type="button"
      className={`admin-align-btn${item.subject_align === val ? " is-active" : ""}`}
      onClick={() => set({ subject_align: val })}
      title={label}
      data-testid={`admin-subject-align-${val}`}
      aria-label={label}
    >{icon}</button>
  );

  const preview = (item.subject || "").trim().length > 0 || !isRichTextEmpty(item.body);

  return (
    <div className="admin-form" data-testid="admin-news-form">
      <h2 className="admin-list-title">{item.id ? "Eintrag bearbeiten" : "Neuen Eintrag anlegen"}</h2>

      <div className="admin-two-col">
        <label className="admin-label">
          Start (Datum + Uhrzeit)
          <input type="datetime-local" value={item.start_at}
                 onChange={(e) => set({ start_at: e.target.value })} required data-testid="admin-news-start"/>
        </label>
        <label className="admin-label">
          Ende (Datum + Uhrzeit)
          <input type="datetime-local" value={item.end_at}
                 onChange={(e) => set({ end_at: e.target.value })} required data-testid="admin-news-end"/>
        </label>
      </div>

      {serverNow && item.start_at && item.end_at && (
        <p className="admin-align-hint" data-testid="news-visibility-hint" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          {serverNow < item.start_at ? (
            <><Clock size={14} /> Noch nicht sichtbar – der Eintrag erscheint erst ab {formatDateTimeForDisplay(item.start_at)} auf der Startseite.</>
          ) : serverNow > item.end_at ? (
            <><EyeOff size={14} /> Aktuell nicht sichtbar – der Zeitraum ist bereits abgelaufen (Ende: {formatDateTimeForDisplay(item.end_at)}).</>
          ) : (
            <><Eye size={14} /> Aktuell auf der Startseite sichtbar.</>
          )}
        </p>
      )}

      <label className="admin-label">
        Betreff
        <input type="text" value={item.subject}
               onChange={(e) => set({ subject: e.target.value })}
               maxLength={200}
               placeholder="z. B. Praxis vom 12.–19.08. geschlossen"
               data-testid="admin-news-subject"
               style={{ textAlign: item.subject_align }}/>
        <div className="admin-align-row">
          <span className="admin-align-hint">Ausrichtung:</span>
          {alignBtn("left", "Links",
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2"/><rect x="1" y="6" width="9" height="2"/><rect x="1" y="10" width="14" height="2"/><rect x="1" y="14" width="9" height="2"/></svg>)}
          {alignBtn("center", "Mitte",
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2"/><rect x="3.5" y="6" width="9" height="2"/><rect x="1" y="10" width="14" height="2"/><rect x="3.5" y="14" width="9" height="2"/></svg>)}
          {alignBtn("right", "Rechts",
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2"/><rect x="6" y="6" width="9" height="2"/><rect x="1" y="10" width="14" height="2"/><rect x="6" y="14" width="9" height="2"/></svg>)}
        </div>
      </label>

      <label className="admin-label">
        Text
        <RichTextEditor value={item.body} onChange={(html) => set({ body: html })}
                        placeholder="Fließtext – mit Fett, Kursiv, Größe und Ausrichtung formatieren." />
      </label>

      <div className="admin-actions-row">
        <button type="button" onClick={onSave} className="admin-btn admin-btn-primary" data-testid="admin-news-save">
          {item.id ? "Änderungen speichern" : "Neuen Eintrag anlegen"}
        </button>
        <button type="button" onClick={onCancel} className="admin-btn admin-btn-ghost" data-testid="admin-news-cancel">
          Abbrechen
        </button>
      </div>

      {preview && (
        <div className="admin-preview">
          <h3>Vorschau</h3>
          <div className="news-banner news-banner--preview">
            <div className="news-banner-inner">
              {item.subject && (
                <h2 className="news-banner-subject" style={{ textAlign: item.subject_align }}>
                  {item.subject}
                </h2>
              )}
              {!isRichTextEmpty(item.body) && (
                <div className="news-banner-body"
                     dangerouslySetInnerHTML={{ __html: renderNewsHtml(item.body) }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------- Benachrichtigungs-E-Mails (E-Mail-Empfänger) -------- */
function NotificationEmails({ readOnly = false }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    const { data } = await api.listEmails();
    setLoading(false);
    if (data && data.ok) setEmails(data.items || []);
  };
  useEffect(() => { reload(); }, []);

  const addEmail = async (e) => {
    e.preventDefault();
    setError("");
    if (!newEmail.trim()) return;
    setBusy(true);
    const { data, status } = await api.addEmail(newEmail.trim());
    setBusy(false);
    if (data && data.ok) { setNewEmail(""); await reload(); return; }
    if (status === 409) setError("Diese E-Mail ist bereits hinterlegt.");
    else if (status === 400) setError(data?.message || "Ungültige E-Mail-Adresse.");
    else setError("Speichern fehlgeschlagen.");
  };

  const remove = async (e) => {
    if (!window.confirm(`E-Mail „${e.email}" wirklich entfernen?`)) return;
    const { data } = await api.deleteEmail(e.id);
    if (data && data.ok) await reload();
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTestMsg("");
    if (!testTo.trim()) return;
    setTestBusy(true);
    const { data } = await api.sendTestMail(testTo.trim());
    setTestBusy(false);
    setTestMsg((data && data.message) || "Unbekannter Status.");
  };

  return (
    <section className="admin-emails" data-testid="admin-emails-section">
      <h2 className="admin-list-title" style={{ marginTop: "2.5rem" }}>
        Benachrichtigungen bei „Eintrag geht live&#8220;
      </h2>
      <p className="admin-subtitle" style={{ marginBottom: "1rem" }}>
        Diese E-Mail-Adressen erhalten eine Nachricht, sobald ein geplanter Eintrag automatisch auf der Startseite erscheint.
      </p>

      <form onSubmit={addEmail} className="admin-emails-add" data-testid="admin-email-add-form">
        <input type="email" placeholder="z. B. praxis@dein-postfach.de"
               value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
               required data-testid="admin-email-input"/>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={busy || readOnly} data-testid="admin-email-add">
          + Hinzufügen
        </button>
      </form>
      {error && <p className="admin-error" style={{ marginTop: "0.5rem" }} data-testid="admin-email-error">{error}</p>}

      {loading ? <p>Lade…</p> : emails.length === 0 ? (
        <p className="admin-empty">Noch keine E-Mail-Adressen hinterlegt.</p>
      ) : (
        <ul className="admin-emails-list" data-testid="admin-emails-list">
          {emails.map((e) => (
            <li key={e.id} data-testid={`admin-email-item-${e.id}`}>
              <span className="admin-email-address">{e.email}</span>
              <button type="button" onClick={() => remove(e)} className="admin-btn admin-btn-danger" data-testid={`admin-email-delete-${e.id}`}>
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="admin-test-mail" style={{ marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px dashed #DFD3B8" }}>
        <p className="admin-align-hint" style={{ margin: "0 0 0.5rem", fontSize: "0.82rem" }}>
          Test-E-Mail versenden (prüft den SMTP-Versand):
        </p>
        <form onSubmit={sendTest} className="admin-emails-add">
          <input type="email" placeholder="Empfänger für Test-Mail"
                 value={testTo} onChange={(ev) => setTestTo(ev.target.value)}
                 required data-testid="admin-test-mail-input"/>
          <button type="submit" className="admin-btn admin-btn-ghost" disabled={testBusy || readOnly} data-testid="admin-test-mail-send">
            {testBusy ? "Sende…" : "Test-Mail senden"}
          </button>
        </form>
        {testMsg && <p className="admin-success" style={{ marginTop: "0.5rem" }} data-testid="admin-test-mail-result">{testMsg}</p>}
      </div>
    </section>
  );
}
